/**
 * EscalateModal — Escalate-to-HOD/Director form in a Modal.
 *
 * Wraps EscalateForm with:
 *   - A Modal container
 *   - A justification textarea (required, ≥ 20 chars)
 *   - On submit: POST /api/v1/leaves/:id/escalate → optimistic status update
 *     → note POST (/api/v1/leaves/:id/note) with justification
 *     → success toast
 *
 * The backend escalate route only accepts `target`; the justification is
 * appended as a leave note in a follow-up call so it appears in the audit trail.
 */
import { useId, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import type { EscalationTarget, Role } from '@lms/shared-types'
import { Modal } from '../primitives/Modal'
import { Field } from '../primitives/Field'
import { Select } from '../forms/Select'
import { Button } from '../primitives/Button'
import { escalateLeave, addLeaveNote } from '../../api/leaves'
import { ApiError } from '../../api/client'
import { escalationTargetsFor } from './EscalateForm'

const ESCALATION_TARGET_LABEL: Record<EscalationTarget, string> = {
  manager: 'Manager',
  hod: 'Head of department',
  director: 'Director',
  exec_director: 'Executive Director',
}

const MIN_JUSTIFICATION = 20

interface EscalateModalProps {
  leaveId: string
  employeeName: string
  viewerRole: Role
  open: boolean
  onClose: () => void
  onSuccess: (leaveId: string) => void
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.status})`
  if (err instanceof Error) return err.message
  return 'Escalation failed'
}

export function EscalateModal({
  leaveId,
  employeeName,
  viewerRole,
  open,
  onClose,
  onSuccess,
}: EscalateModalProps): JSX.Element {
  const targets = escalationTargetsFor(viewerRole)
  const [target, setTarget] = useState<EscalationTarget>(targets[0] ?? 'hod')
  const [justification, setJustification] = useState('')
  const [error, setError] = useState<string | null>(null)
  const targetFieldId = useId()
  const justFieldId = useId()

  const isJustificationValid = justification.trim().length >= MIN_JUSTIFICATION

  const mutation = useMutation({
    mutationFn: async ({
      id,
      t,
      note,
    }: {
      id: string
      t: EscalationTarget
      note: string
    }) => {
      await escalateLeave(id, t)
      // Best-effort: append justification as a leave note for audit trail.
      // Failure here must not roll back the escalation.
      try {
        await addLeaveNote(id, `Escalation justification: ${note}`)
      } catch {
        // swallow — the escalation already succeeded
      }
    },
    onSuccess: () => {
      setError(null)
      onSuccess(leaveId)
      onClose()
      resetForm()
    },
    onError: (err: unknown) => {
      setError(formatError(err))
    },
  })

  function resetForm(): void {
    setTarget(targets[0] ?? 'hod')
    setJustification('')
    setError(null)
  }

  function handleClose(): void {
    onClose()
    resetForm()
  }

  function handleSubmit(): void {
    if (!isJustificationValid || mutation.isPending) return
    mutation.mutate({ id: leaveId, t: target, note: justification.trim() })
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`Escalate — ${employeeName}`}
      maxWidth={480}
    >
      <div
        data-testid="escalate-modal-body"
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <Field label="Escalate to" htmlFor={targetFieldId} required>
          <Select
            id={targetFieldId}
            value={target}
            onChange={(e) => setTarget(e.target.value as EscalationTarget)}
            data-testid="escalate-target"
            options={targets.map((t) => ({
              value: t,
              label: ESCALATION_TARGET_LABEL[t],
            }))}
          />
        </Field>

        <Field
          label={`Justification (min. ${MIN_JUSTIFICATION} characters)`}
          htmlFor={justFieldId}
          required
        >
          <textarea
            id={justFieldId}
            data-testid="escalate-justification"
            rows={4}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            placeholder="Explain why this leave requires escalation…"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid var(--border)',
              borderRadius: 8,
              background: 'var(--surface)',
              fontFamily: 'inherit',
              fontSize: 13,
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </Field>
        {justification.length > 0 && !isJustificationValid ? (
          <span
            style={{
              fontSize: 11,
              color: 'var(--danger-fg)',
              marginTop: -8,
              display: 'block',
            }}
          >
            {MIN_JUSTIFICATION - justification.trim().length} more character
            {MIN_JUSTIFICATION - justification.trim().length === 1 ? '' : 's'} required
          </span>
        ) : null}

        {error ? (
          <div
            role="alert"
            data-testid="escalate-error"
            style={{
              padding: '8px 10px',
              background: 'var(--danger-bg)',
              border: '1px solid var(--danger-line)',
              borderRadius: 8,
              fontSize: 12,
              color: 'var(--danger-fg)',
            }}
          >
            {error}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            data-testid="confirm-escalate"
            disabled={!isJustificationValid || mutation.isPending}
            onClick={handleSubmit}
          >
            {mutation.isPending ? 'Escalating…' : 'Confirm escalate'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
